import React, { useEffect, useState, useMemo } from "react";
import FlexBetween from "../../components/FlexBetween";
import {
  Box,
  Grid,
  Select,
  MenuItem,
  Checkbox,
  Input,
  Tooltip,
  Button,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useToast } from "../../services/utils";
import DataTable from "../../components/DataGrids/DataGrid";
import { datatypes, dateTimeFormat } from "../../services/constants";
import { useNavigate, useParams } from "react-router-dom";
import useFetchDomainById from "../../hooks/useFetchDomainById";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import CloneDomainDialog from "../../components/Dialogs/CloneDomainDialog";
import { cloneDomain } from "../../services/domainsService";
import { toast } from "react-toastify";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import { IconBtnEditBase } from "../../common/utils/icons";
import Loader from "../../components/Molecules/Loader/Loader";
import { useAuth } from "../../contexts/AuthContext";
import { ViewAuthTags } from "../../components/AuthTags";
import { useAuthTagContext } from "../../contexts/TagAndSystemProvider";
import useFetchActiveAuthTagsList from "../../hooks/useFetchActiveAuthTagsList";

const ViewDomain: React.FC = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { id: currentDomainId } = useParams();

  // Permission checks for domain actions
  const canAddDomain = hasPermission("domain", "add"); // For clone functionality
  const canEditDomain = hasPermission("domain", "edit");
  const [fileData, setFileData] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>({
    domain_name: "",
    domain_code: "",
    domain_desc: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGridLoading, setIsGridLoading] = useState<boolean>(false);
  const [cloneDomainDialog, setCloneDomainDialog] = useState<boolean>(false);
  const [cloneDomainData, setCloneDomainData] = useState<any>({
    domain_name: "",
    domain_code: "",
    domain_desc: "",
    domain_tags: [],
  });

  const [domainData] = useFetchDomainById({
    setIsLoading: setIsGridLoading,
    currentDomainId,
  });
  // Get the setter function from context (following linkedServicesData pattern)
  const { setAllAuthTagsData } = useAuthTagContext();
  // Extract auth tag IDs from domain_tags_details
  const authTagIds = useMemo(() => {
    if (
      !domainData?.domain_tags_details ||
      !Array.isArray(domainData.domain_tags_details)
    ) {
      return [];
    }
    return domainData.domain_tags_details.map(
      (tagDetail: any) => tagDetail.auth_tag_id,
    );
  }, [domainData?.domain_tags_details]);

  // Fetch only the specific auth tags needed for this domain (optimized approach)
  const [authTagsData] = useFetchActiveAuthTagsList({
    setIsLoading,
    // authTagIds,
    enabled: true,
  });
  // Update context state when auth tags data is fetched (following linkedServicesData pattern)
  useEffect(() => {
    if (authTagsData && Array.isArray(authTagsData)) {
      setAllAuthTagsData(authTagsData);
    }
  }, [authTagsData, setAllAuthTagsData]);

  useEffect(() => {
    if (domainData) {
      setFormData({
        domain_name: domainData?.domain_name,
        domain_code: domainData?.domain_code,
        domain_desc: domainData?.domain_desc,
      });
      setCurrentBreadcrumbPage({
        name: domainData?.domain_name,
        id: domainData?.id,
      });
    }
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [domainData]);

  useEffect(() => {
    if (domainData?.domain_properties?.columns?.length > 0) {
      const newFileData = domainData?.domain_properties?.columns?.map(
        (fileItem: any, index: number) => {
          return {
            ...fileItem,
            id: index,
            key: domainData?.domain_properties?.unique_columns?.includes(
              fileItem.name,
            )
              ? true
              : false,
          };
        },
      );

      setFileData(newFileData);
    }
  }, [domainData]);

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      renderCell: (params: any) => <></>,
    },
    {
      field: "name",
      headerName: "Column Name",
      width: 170,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "datatype",
      headerName: "Data Type",
      width: 150,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "mandatory",
      headerName: "Mandatory",
      maxWidth: 90,
      renderCell: (params: any) => {
        return (
          <Checkbox
            checked={
              params.value &&
              (params.value === "Y" ||
                params.value === "y" ||
                params.value === "Yes" ||
                params.value === "yes" ||
                params.value === true)
            }
            sx={{
              "&.Mui-checked": {
                color: "#FFA500",
              },
            }}
            disabled
          />
        );
      },
    },
    {
      field: "key",
      headerName: "Key",
      maxWidth: 60,
      renderCell: (params: any) => {
        return (
          <Checkbox
            checked={
              params.value &&
              (params.value === "Y" ||
                params.value === "y" ||
                params.value === "Yes" ||
                params.value === "yes" ||
                params.value === true)
            }
            sx={{
              "&.Mui-checked": {
                color: "#FFA500",
              },
            }}
            disabled
          />
        );
      },
    },
    {
      field: "format",
      headerName: "Format",
      width: 150,
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Tooltip
            title={dateTimeFormat.includes(params.value) ? params.value : null}
            placement="top"
            arrow
          >
            <span>{params.value}</span>
          </Tooltip>
        );
      },
    },
  ];

  let rows: any = [];
  if (fileData) {
    rows = fileData.map((row, key) => ({
      id: row.id,
      name: row.name,
      datatype: row.datatype,
      mandatory: row.mandatory,
      key: row.key,
      format: row.format,
    }));
  }

  const handleFormData = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };
  const handleCloseCloneDomain = () => {
    setCloneDomainDialog(false);
    setCloneDomainData({
      domain_name: "",
      domain_code: "",
      domain_desc: "",
      domain_tags: [],
    });
  };
  const onSaveCloneDomain = () => {
    const reqBody = {
      domain_id: Number(currentDomainId),
      domain_name: cloneDomainData.domain_name,
      domain_code: cloneDomainData.domain_code,
      domain_desc: cloneDomainData.domain_desc || "",
      domain_tags: cloneDomainData.domain_tags || [],
    };
    cloneDomain(reqBody)
      .then((response) => {
        showToast("Domain cloned successfully!", "success");
        navigate(`/domain/view/${response?.id}`);
      })
      .catch((error) => {
        showToast(`Cannot create clone Domain`, "error");
      });
    handleCloseCloneDomain();
  };
  return (
    <Box>
      {isGridLoading && <Loader isLoading={isGridLoading} />}
      <Box className="text-box-card compact-text-box-card">
        <Grid container rowSpacing={1.5} columnSpacing={2.5}>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <label className="label-text text-bold"> Domain Name</label>
            <div className="form-control word-break-all">
              {formData.domain_name || "N/A"}
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <label className="label-text text-bold"> Domain Code</label>
            <div className="form-control word-break-all">
              {formData.domain_code || "N/A"}
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <label className="label-text text-bold">Tag(s)</label>
            <ViewAuthTags
              entityTagsDetails={domainData?.domain_tags_details || []}
              entityName="Domain"
              showToast={showToast}
            />
          </Grid>
          {formData.domain_desc && (
            <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
              <label className="label-text text-bold"> Description</label>
              <div className="form-control word-break-all">
                {formData.domain_desc || "N/A"}
              </div>
            </Grid>
          )}
          <Grid
            item
            xs
            sx={{
              justifyContent: "flex-end",
              display: "flex",
              alignItems: "center",
              columnGap: "16px",
            }}
          >
            {canAddDomain && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  // Auto-populate clone dialog with current domain data
                  setCloneDomainData({
                    domain_name: `Copy_${domainData?.domain_name || ""}`,
                    domain_code: `Copy_${domainData?.domain_code || ""}`,
                    domain_desc: domainData?.domain_desc || "",
                    domain_tags:
                      domainData?.domain_tags_details?.map(
                        (tag: any) => tag.auth_tag_id,
                      ) || [],
                  });
                  setCloneDomainDialog(true);
                }}
                className="btn-orange btn-nostyle"
              >
                clone
              </Button>
            )}
            {canEditDomain && (
              <button
                className="btn-nostyle icon-btn-edit"
                onClick={() => navigate(`/domain/edit/${currentDomainId}`)}
              >
                <IconBtnEditBase ariaLabel="Edit domain" />
              </button>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box>
        <FlexBetween gap="3rem">
          <DataTable
            dataColumns={columns}
            dataRows={fileData}
            checkboxSelection={false}
            loading={isLoading}
            dataListTitle={"Columns List"}
            className="dataTable no-radius hide-progress-icon"
          />
        </FlexBetween>
      </Box>
      {canAddDomain && (
        <CloneDomainDialog
          cloneDomainDialog={cloneDomainDialog}
          handleCloseCloneDomain={handleCloseCloneDomain}
          cloneDomainData={cloneDomainData}
          setCloneDomainData={setCloneDomainData}
          onSaveCloneDomain={onSaveCloneDomain}
          setIsLoading={setIsLoading}
        />
      )}
    </Box>
  );
};

export default ViewDomain;
